/* №5 «Тайлан» — server тайлан (дизайны reports.jsx-ээс) */
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cwApi, type CwReportDto } from '@/api/carwash';
import { tug } from '@/shared/format';
import type { LucideIcon } from '@/shared/icons';
import { IconCar, IconReceipt, IconWallet } from '@/shared/icons';
import { useShellUi } from '@/features/shell/ShellLayout';

function KpiCard({ Icon, label, value }: { Icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="rounded-[var(--r)] bg-white ring-1 ring-slate-900/5 shadow-xs p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="w-9 h-9 rounded-[10px] bg-cyan-50 text-[var(--brand1)] flex items-center justify-center"><Icon size={18} /></span>
        <span className="text-[13px] font-semibold text-slate-500">{label}</span>
      </div>
      <div className="text-[26px] font-extrabold text-slate-900 tracking-tight leading-none">{value}</div>
    </div>
  );
}

function Panel({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={'rounded-[var(--r)] bg-white ring-1 ring-slate-900/5 shadow-xs p-4 flex flex-col gap-3 ' + (className || '')}>
      <div className="text-[14px] font-bold text-slate-800">{title}</div>
      {children}
    </div>
  );
}

function WeekChart({ week }: { week: CwReportDto['Week'] }) {
  const max = Math.max(1, ...week.map((w) => w.Revenue));
  const last = week.length - 1;
  return (
    <div className="flex items-end justify-between gap-2 h-[180px] pt-4">
      {week.map((w, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-2 h-full justify-end group">
          <div className="text-[10px] font-bold text-slate-500 opacity-0 group-hover:opacity-100 transition">{Math.round(w.Revenue / 1000)}k</div>
          <div className="w-full rounded-t-[6px] transition-all relative"
            style={{
              height: `${(w.Revenue / max) * 100}%`,
              minHeight: '3px',
              background: i === last ? 'linear-gradient(to top, var(--brand1), var(--brand2))' : '#e2e8f0',
            }}>
          </div>
          <div className={'text-[11px] font-semibold ' + (i === last ? 'text-[var(--brand1)]' : 'text-slate-400')}>{w.Day}</div>
        </div>
      ))}
    </div>
  );
}

function TopServices({ items }: { items: CwReportDto['TopServices'] }) {
  const max = Math.max(1, ...items.map((s) => s.Revenue));
  if (items.length === 0) return <div className="text-[13px] text-slate-400 py-4 text-center">Өгөгдөл алга</div>;
  return (
    <div className="flex flex-col gap-3">
      {items.map((s) => (
        <div key={s.Name} className="flex flex-col gap-1.5">
          <div className="flex items-baseline justify-between">
            <span className="text-[13px] font-medium text-slate-700">{s.Name}</span>
            <span className="text-[13px] font-bold text-slate-900">{tug(s.Revenue)} <span className="text-[11px] font-medium text-slate-400">· {s.Count}ш</span></span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)]" style={{ width: `${(s.Revenue / max) * 100}%` }}></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function WasherPerf({ items }: { items: CwReportDto['WasherPerf'] }) {
  const max = Math.max(1, ...items.map((w) => w.Cars));
  if (items.length === 0) return <div className="text-[13px] text-slate-400 py-4 text-center">Өгөгдөл алга</div>;
  return (
    <div className="flex flex-col gap-2.5">
      {items.map((w) => (
        <div key={w.Name} className="flex items-center gap-3">
          <div className="flex items-center gap-2 w-[100px] shrink-0">
            <span className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 text-[12px] font-bold flex items-center justify-center">{w.Name[0]}</span>
            <span className="text-[13px] font-medium text-slate-700 truncate">{w.Name}</span>
          </div>
          <div className="flex-1 h-7 rounded-[8px] bg-slate-100 overflow-hidden flex items-center">
            <div className="h-full rounded-[8px] bg-cyan-200 flex items-center justify-end pr-2" style={{ width: `${(w.Cars / max) * 100}%` }}>
              <span className="text-[12px] font-bold text-cyan-800">{w.Cars}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function HourlyChart({ items }: { items: CwReportDto['Hourly'] }) {
  const max = Math.max(1, ...items.map((h) => h.Cars));
  if (items.length === 0) return <div className="text-[13px] text-slate-400 py-4 text-center">Өгөгдөл алга</div>;
  return (
    <div className="flex items-end justify-between gap-1.5 h-[120px]">
      {items.map((h) => (
        <div key={h.Hour} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
          <div className="w-full rounded-t-[4px] bg-cyan-300/80" style={{ height: `${(h.Cars / max) * 100}%`, minHeight: '4px' }}></div>
          <div className="text-[10px] font-medium text-slate-400">{String(h.Hour).padStart(2, '0')}</div>
        </div>
      ))}
    </div>
  );
}

const DAY_NAMES = ['Ням', 'Даваа', 'Мягмар', 'Лхагва', 'Пүрэв', 'Баасан', 'Бямба'];

export function ReportsPage() {
  const { isMobile: mobile } = useShellUi();
  const reportQ = useQuery({ queryKey: ['cw', 'report'], queryFn: cwApi.report });
  const r = reportQ.data;

  const now = new Date();
  const dateLabel = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} · ${DAY_NAMES[now.getDay()]}`;

  if (!r) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400 text-sm">
        {reportQ.isLoading ? 'Ачааллаж байна…' : 'Тайлан уншигдсангүй'}
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className={'mx-auto flex flex-col gap-4 ' + (mobile ? 'p-4 pb-24' : 'p-6 max-w-[1100px]')}>
        <div className="flex items-baseline justify-between">
          <h1 className="text-[20px] font-extrabold text-slate-900">Тайлан</h1>
          <span className="text-[13px] text-slate-400 font-medium">Өнөөдөр · {dateLabel}</span>
        </div>

        {/* KPI */}
        <div className={'grid gap-3 ' + (mobile ? 'grid-cols-1' : 'grid-cols-3')}>
          <KpiCard Icon={IconWallet} label="Өнөөдрийн орлого" value={tug(r.Today.Revenue)} />
          <KpiCard Icon={IconCar} label="Угаасан машин" value={`${r.Today.Cars}`} />
          <KpiCard Icon={IconReceipt} label="Дундаж чек" value={tug(r.Today.AvgTicket)} />
        </div>

        {/* 7 хоног */}
        <Panel title="7 хоногийн орлого">
          <WeekChart week={r.Week} />
        </Panel>

        {/* топ үйлчилгээ + угаагч */}
        <div className={'grid gap-4 ' + (mobile ? 'grid-cols-1' : 'grid-cols-2')}>
          <Panel title="Үйлчилгээний топ"><TopServices items={r.TopServices} /></Panel>
          <Panel title="Угаагч бүрийн гүйцэтгэл"><WasherPerf items={r.WasherPerf} /></Panel>
        </div>

        {/* цагийн ачаалал */}
        <Panel title="Цагийн ачаалал (машины тоо)">
          <HourlyChart items={r.Hourly} />
        </Panel>
      </div>
    </div>
  );
}
